// Sportsbook identity and logo registry. The deployed sportsbook master list is the
// only image source; missing entries render as text instead of invented artwork.

const configuredMasterListBase = import.meta.env?.VITE_SPORTSBOOK_MASTER_LIST_BASE

export const MASTER_LIST_BASE = `${configuredMasterListBase || 'https://sportsbook-master-list.vercel.app/logos/'}`
  .replace(/\/*$/, '/')

export const BOOK_DISPLAY_ORDER = Object.freeze([
  'fanduel',
  'draftkings',
  'draftkings_pick6',
  'betmgm',
  'caesars',
  'espnbet',
  'thescore',
  'fanatics',
  'betrivers',
  'ballybet',
  'betsafe',
  'nordicbet',
  'hardrock',
  'fliff',
  'bovada',
  'betonline',
  'betanything',
  'betr_au',
  'betsson',
  'playup',
  'coral',
  'ladbrokes',
  'grosvenor',
  'betparx',
  'atg',
  'svenskaspel',
  'livescorebet',
  'virginbet',
  'tabtouch',
  'netbet',
  'unibet',
  'unibet_au',
  'unibet_fr',
  'betway',
  'winamax',
  'mybookie',
  'everygame',
  'pmu',
  'marathonbet',
  'pinnacle',
  'kalshi',
  'polymarket',
  'prizepicks',
  'underdog',
  'betopenly',
  'matchbook',
  'novig',
  'prophetx',
  'rebet',
  'smarkets',
  'sporttrade',
])

export const BOOK_DISPLAY_NAMES = Object.freeze({
  fanduel: 'FanDuel',
  draftkings: 'DraftKings',
  draftkings_pick6: 'DraftKings Pick6',
  betmgm: 'BetMGM',
  caesars: 'Caesars',
  espnbet: 'ESPN BET',
  thescore: 'theScore',
  fanatics: 'Fanatics',
  betrivers: 'BetRivers',
  ballybet: 'Bally Bet',
  betsafe: 'Betsafe',
  nordicbet: 'NordicBet',
  hardrock: 'Hard Rock Bet',
  fliff: 'Fliff',
  bovada: 'Bovada',
  betonline: 'BetOnline',
  betanything: 'BetAnything',
  betr_au: 'Betr Australia',
  betsson: 'Betsson',
  playup: 'PlayUp',
  coral: 'Coral',
  ladbrokes: 'Ladbrokes',
  grosvenor: 'Grosvenor',
  betparx: 'betPARX',
  atg: 'ATG',
  svenskaspel: 'Svenska Spel',
  livescorebet: 'LiveScore Bet',
  virginbet: 'Virgin Bet',
  tabtouch: 'TABtouch',
  netbet: 'NetBet',
  unibet: 'Unibet',
  unibet_au: 'Unibet Australia',
  unibet_fr: 'Unibet France',
  betway: 'Betway',
  winamax: 'Winamax',
  mybookie: 'MyBookie',
  everygame: 'Everygame',
  pmu: 'PMU',
  marathonbet: 'Marathonbet',
  pinnacle: 'Pinnacle',
  kalshi: 'Kalshi',
  polymarket: 'Polymarket',
  prizepicks: 'PrizePicks',
  underdog: 'Underdog Fantasy',
  betopenly: 'BetOpenly',
  matchbook: 'Matchbook',
  novig: 'Novig',
  prophetx: 'ProphetX',
  rebet: 'ReBet',
  smarkets: 'Smarkets',
  sporttrade: 'Sporttrade',
})

// Canonical board slug -> proof-backed file from the deployed master-list manifest.
// Blank/missing entries are intentionally omitted; the UI must show their real name.
export const BOARD_LOGO_FILES = Object.freeze({
  fanduel: 'fanduel.png',
  draftkings: 'draftkings.png',
  betmgm: 'betmgm_com_uk.png',
  caesars: 'caesars.png',
  thescore: 'thescore.svg',
  fanatics: 'fanatics_com_google.png',
  betrivers: 'betrivers.png',
  ballybet: 'ballybet.png',
  betsafe: 'betsafe_com.svg',
  hardrock: 'hardrock_bet.svg',
  fliff: 'fliff_com.png',
  bovada: 'bovada.png',
  betonline: 'betonline.png',
  betr_au: 'betr_com_au.png',
  betsson: 'betsson_com.png',
  playup: 'playup.png',
  coral: 'coral_co_uk.png',
  ladbrokes: 'ladbrokes_com.png',
  grosvenor: 'grosvenorcasinos_com.png',
  betparx: 'betparx_com_fixed.png',
  atg: 'atg_se.svg',
  svenskaspel: 'svenskaspel_se_fixed.png',
  livescorebet: 'livescorebet_com.png',
  virginbet: 'virginbet_com.ico',
  tabtouch: 'tabtouch_com_au_fixed.png',
  netbet: 'netbet.ico',
  unibet: 'unibet_com_horse.png',
  unibet_au: 'unibet_com_horse.png',
  unibet_fr: 'unibet_com_horse.png',
  betway: 'betway_com_horse.png',
  winamax: 'winamax_fr.png',
  mybookie: 'mybookie.png',
  everygame: 'everygame_com.png',
  marathonbet: 'marathonbet_com.png',
  pinnacle: 'pinnacle_com.png',
  kalshi: 'kalshi_com.svg',
  polymarket: 'polymarket.png',
  prizepicks: 'prizepicks_com.svg',
  underdog: 'underdogfantasy_com.svg',
  betopenly: 'betopenly_com_fixed.png',
  matchbook: 'matchbook_com.svg',
  novig: 'novig_com.png',
  prophetx: 'prophetx_co.png',
  rebet: 'rebet_app.png',
  smarkets: 'smarkets_com_fixed.png',
  sporttrade: 'sporttrade_com_google.png',
})

// Exact jobs-provider identity -> proof-backed raster asset. Employer names do
// not always match sportsbook names, so these aliases must be explicit rather
// than inferred from display strings.
export const JOB_COMPANY_LOGOS = Object.freeze({
  "Bally's Corporation": Object.freeze({ masterKey: 'Bally Bet', file: 'ballybet.png' }),
  bet365: Object.freeze({ masterKey: 'bet365', file: 'bet365_com.webp' }),
  BetMGM: Object.freeze({ masterKey: 'BetMGM', file: 'betmgm_com_uk.png' }),
  Betr: Object.freeze({ masterKey: 'Betr', file: 'betr_app.png' }),
  'BetRivers (Rush Street Interactive)': Object.freeze({ masterKey: 'BetRivers', file: 'betrivers.png' }),
  Betway: Object.freeze({ masterKey: 'Betway', file: 'betway_com_horse.png' }),
  Caesars: Object.freeze({ masterKey: 'Caesars', file: 'caesars.png' }),
  Chalk: Object.freeze({ masterKey: 'Chalkboard', file: 'chalkboard_io.png' }),
  Dabble: Object.freeze({ masterKey: 'Dabble (Australia)', file: 'dabble_com.webp' }),
  DraftKings: Object.freeze({ masterKey: 'DraftKings', file: 'draftkings.png' }),
  'Fanatics Betting & Gaming': Object.freeze({ masterKey: 'Fanatics', file: 'fanatics_com_google.png' }),
  FanDuel: Object.freeze({ masterKey: 'FanDuel', file: 'fanduel.png' }),
  Fliff: Object.freeze({ masterKey: 'Fliff', file: 'fliff_com.png' }),
  'Hard Rock Bet': Object.freeze({ masterKey: 'Hard Rock Bet (PNG)', file: 'hardrock_bet.png' }),
  Kalshi: Object.freeze({ masterKey: 'Kalshi (PNG)', file: 'kalshi_com_256.png' }),
  Kambi: Object.freeze({ masterKey: 'Kambi', file: 'kambi_com.png' }),
  Novig: Object.freeze({ masterKey: 'Novig', file: 'novig_com.png' }),
  'PENN Interactive (ESPN BET / theScore)': Object.freeze({ masterKey: 'theScore (PNG)', file: 'thescore.png' }),
  PrizePicks: Object.freeze({ masterKey: 'PrizePicks (PNG)', file: 'prizepicks_com.png' }),
  ProphetX: Object.freeze({ masterKey: 'Prophet X', file: 'prophetx_co.png' }),
  Rebet: Object.freeze({ masterKey: 'Rebet', file: 'rebet_app.png' }),
  Sleeper: Object.freeze({ masterKey: 'Sleeper', file: 'sleeper_com.png' }),
  'Underdog Fantasy': Object.freeze({ masterKey: 'Underdog (PNG)', file: 'underdogfantasy_com.png' }),
  'Wynn Resorts': Object.freeze({ masterKey: 'WynnBET', file: 'wynnbet_com.png' }),
})

function aliasKey(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function fallbackSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

const EXTRA_ALIASES = Object.freeze({
  bally: 'ballybet',
  ballys: 'ballybet',
  ballysportsbook: 'ballybet',
  betr: 'betr_au',
  betraustralia: 'betr_au',
  hardrockbet: 'hardrock',
  hardrocksportsbook: 'hardrock',
  ladbrokesaustralia: 'ladbrokes',
  thescorebet: 'thescore',
  unibetaustralia: 'unibet_au',
  unibetau: 'unibet_au',
  unibetfrance: 'unibet_fr',
  unibetfr: 'unibet_fr',
  underdogfantasy: 'underdog',
  prophetxexchange: 'prophetx',
})

const BOOK_ALIAS_TO_SLUG = (() => {
  const aliases = { ...EXTRA_ALIASES }
  for (const slug of BOOK_DISPLAY_ORDER) {
    aliases[aliasKey(slug)] = slug
    aliases[aliasKey(BOOK_DISPLAY_NAMES[slug])] = slug
  }
  return Object.freeze(aliases)
})()

/** Resolve source/display aliases to one stable slug while preserving regional variants. */
export function canonicalBookSlug(value) {
  if (!value) return ''
  return BOOK_ALIAS_TO_SLUG[aliasKey(value)] || fallbackSlug(value)
}

export function getBookDisplayName(value) {
  const slug = canonicalBookSlug(value)
  return BOOK_DISPLAY_NAMES[slug] || String(value || slug)
}

/** Canonical slug/display/logo identity. A missing logo URL is an explicit registry gap. */
export function resolveBookIdentity(value) {
  const slug = canonicalBookSlug(value)
  const file = BOARD_LOGO_FILES[slug] || null
  return {
    slug,
    displayName: BOOK_DISPLAY_NAMES[slug] || String(value || slug),
    logoFile: file,
    logoUrl: file ? `${MASTER_LIST_BASE}${file}` : null,
    logoStatus: file ? 'verified-master-list' : 'missing-master-list',
  }
}

export function bookLogoSrc(value) {
  return resolveBookIdentity(value).logoUrl
}

/** Resolve an exact jobs provider to its audited sportsbook-master-list asset. */
export function resolveJobCompanyLogo(company) {
  const provider = String(company || '')
  const record = JOB_COMPANY_LOGOS[provider]
  if (!record) {
    return {
      company: provider,
      masterKey: null,
      logoFile: null,
      logoUrl: null,
      logoStatus: 'missing-master-list-alias',
    }
  }
  return {
    company: provider,
    masterKey: record.masterKey,
    logoFile: record.file,
    logoUrl: `${MASTER_LIST_BASE}${record.file}`,
    logoStatus: 'verified-master-list',
  }
}

export function jobCompanyLogoSrc(company) {
  return resolveJobCompanyLogo(company).logoUrl
}

/** Coverage counts canonical identities, not duplicate aliases. */
export function buildLogoRegistry(values) {
  const slugs = Array.from(new Set(values.map(canonicalBookSlug).filter(Boolean)))
  const resolved = {}
  const missing = []

  for (const slug of slugs) {
    const identity = resolveBookIdentity(slug)
    if (identity.logoFile) {
      resolved[slug] = {
        file: identity.logoFile,
        url: identity.logoUrl,
        source: 'sportsbook-master-list',
      }
    } else {
      missing.push(slug)
    }
  }

  missing.sort()
  const n = Object.keys(resolved).length
  return {
    resolved,
    missing,
    source: 'sportsbook-master-list.vercel.app/manifest.json',
    coverage: {
      resolved: n,
      total: slugs.length,
      pct: slugs.length ? Math.round((1000 * n) / slugs.length) / 10 : 100,
    },
  }
}
